import { useState } from 'react';
import { ArrowLeft, ExternalLink, ArrowRight, Star, TrendingUp, Users, Clock, Award } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Chatbot from '../components/Chatbot';
import SEO from '../components/SEO';

const portfolioProjects = [
  {
    id: 1,
    title: "MedConnect Pro",
    category: "Healthcare",
    client: "Regional Healthcare Network",
    description: "A comprehensive patient management platform that streamlined appointment scheduling, medical records, and telehealth services for a network of 15 clinics.",
    challenge: "The healthcare network was struggling with fragmented patient data across multiple systems, leading to scheduling conflicts and delayed care delivery.",
    solution: "We built a unified cloud-based platform with real-time data synchronization, HIPAA-compliant video consultations, and AI-powered appointment optimization.",
    results: [
      { metric: "40%", label: "Reduction in wait times" },
      { metric: "60%", label: "Faster patient check-in" },
      { metric: "98%", label: "Patient satisfaction" },
      { metric: "3x", label: "Telehealth adoption" }
    ],
    technologies: ["React", "Node.js", "MongoDB", "AWS", "Twilio", "AI/ML"],
    image: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&auto=format&fit=crop&q=60",
    testimonial: {
      quote: "CLOUD IT transformed our patient experience. The new system has been a game-changer for our staff and patients alike.",
      author: "Dr. Sarah Mitchell",
      role: "Chief Medical Officer"
    },
    color: "#10B981",
    duration: "4 months"
  },
  {
    id: 2,
    title: "ShopFlow E-Commerce",
    category: "E-Commerce",
    client: "Fashion Retail Brand",
    description: "A modern e-commerce platform with AI-powered product recommendations, real-time inventory management, and seamless multi-channel integration.",
    challenge: "The client's legacy system couldn't handle peak traffic, resulting in lost sales and poor customer experience during promotional events.",
    solution: "We developed a scalable microservices architecture with intelligent caching, personalized shopping experiences, and integrated analytics dashboard.",
    results: [
      { metric: "250%", label: "Increase in conversions" },
      { metric: "45%", label: "Higher average order value" },
      { metric: "99.9%", label: "Uptime during sales" },
      { metric: "2.5M", label: "Monthly active users" }
    ],
    technologies: ["Next.js", "Python", "PostgreSQL", "Redis", "Stripe", "AI/ML"],
    image: "https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&auto=format&fit=crop&q=60",
    testimonial: {
      quote: "Our online revenue has tripled since launching the new platform. The team delivered beyond our expectations.",
      author: "Michael Torres",
      role: "CEO, Fashion Forward Inc."
    },
    color: "#8B5CF6",
    duration: "5 months"
  },
  {
    id: 3,
    title: "InnovateTech Dashboard",
    category: "Tech Startup",
    client: "SaaS Analytics Company",
    description: "A real-time analytics dashboard with custom data visualizations, automated reporting, and predictive insights for enterprise clients.",
    challenge: "The startup needed to differentiate from competitors with a powerful yet intuitive analytics solution that could scale with their growing customer base.",
    solution: "We created a modern dashboard with drag-and-drop customization, real-time data streaming, and AI-powered anomaly detection with beautiful visualizations.",
    results: [
      { metric: "500+", label: "Enterprise clients" },
      { metric: "85%", label: "User retention rate" },
      { metric: "10x", label: "Faster report generation" },
      { metric: "$5M", label: "Series A funding raised" }
    ],
    technologies: ["React", "D3.js", "FastAPI", "TimescaleDB", "WebSockets", "TensorFlow"],
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=800&auto=format&fit=crop&q=60",
    testimonial: {
      quote: "The dashboard CLOUD IT built helped us secure our Series A. Investors were blown away by the product quality.",
      author: "Jennifer Park",
      role: "Founder & CTO"
    },
    color: "#3B82F6",
    duration: "6 months"
  },
  {
    id: 4,
    title: "SecurePay Fintech",
    category: "Fintech",
    client: "Payment Processing Startup",
    description: "A secure payment processing platform with fraud detection, multi-currency support, and seamless API integration for businesses worldwide.",
    challenge: "The fintech startup needed a robust, PCI-compliant payment system that could handle high transaction volumes while maintaining security.",
    solution: "We built a distributed payment processing system with real-time fraud detection using ML models, automated compliance monitoring, and developer-friendly APIs.",
    results: [
      { metric: "$2B+", label: "Transactions processed" },
      { metric: "0.01%", label: "Fraud rate" },
      { metric: "99.99%", label: "System uptime" },
      { metric: "150+", label: "Countries supported" }
    ],
    technologies: ["Go", "Kubernetes", "PostgreSQL", "Redis", "Machine Learning", "Blockchain"],
    image: "https://images.unsplash.com/photo-1563013544-824ae1b704d3?w=800&auto=format&fit=crop&q=60",
    testimonial: {
      quote: "CLOUD IT's expertise in fintech security gave us the confidence to scale globally. They're true partners in our success.",
      author: "David Chen",
      role: "Co-founder, SecurePay"
    },
    color: "#F97316",
    duration: "8 months"
  }
];

const categories = ["All", "Healthcare", "E-Commerce", "Tech Startup", "Fintech"];

const Portfolio = () => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState("All");
  const [selectedProject, setSelectedProject] = useState(null);

  const filteredProjects = activeCategory === "All" 
    ? portfolioProjects 
    : portfolioProjects.filter(p => p.category === activeCategory);

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-dark)' }}>
      <SEO title="Portfolio - Case Studies & Success Stories" />
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-[600px] h-[600px] bg-[#3B82F6]/10 rounded-full blur-[150px]"></div>
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#8B5CF6]/10 rounded-full blur-[120px]"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-[7.6923%]">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-[#3B82F6] hover:text-[var(--text-primary)] transition-colors duration-300 mb-8"
          >
            <ArrowLeft size={20} />
            <span>Back to Home</span>
          </button>

          <div className="max-w-4xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#8B5CF6]/10 border border-[#8B5CF6]/30 rounded-full mb-6">
              <Award size={16} className="text-[#8B5CF6]" />
              <span className="text-[#8B5CF6] text-sm font-semibold">Our Work</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-[var(--text-primary)] mb-6 leading-tight" style={{ letterSpacing: '-0.02em' }}>
              Projects That{' '}
              <span className="bg-gradient-to-r from-[#3B82F6] via-[#8B5CF6] to-[#F97316] bg-clip-text text-transparent">
                Drive Results
              </span>
            </h1>
            
            <p className="text-xl text-[var(--text-secondary)] max-w-3xl leading-relaxed">
              Explore our portfolio of successful projects across healthcare, e-commerce, fintech, and technology startups. Real solutions, measurable impact.
            </p>
          </div>
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-8 px-[7.6923%]">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap gap-3">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-6 py-3 rounded-full font-medium transition-all duration-300 ${
                  activeCategory === category
                    ? 'bg-gradient-to-r from-[#3B82F6] to-[#8B5CF6] text-white shadow-lg shadow-[#8B5CF6]/25'
                    : 'bg-[var(--bg-card)] text-[var(--text-muted)] border border-[var(--border)] hover:border-[#8B5CF6]/50'
                }`}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Projects Grid */}
      <section className="py-16 px-[7.6923%]">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {filteredProjects.map((project) => (
              <div
                key={project.id}
                className="group bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl overflow-hidden hover:border-[var(--border-hover)] transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl cursor-pointer"
                onClick={() => setSelectedProject(project)}
              >
                {/* Project Image */}
                <div className="relative h-64 overflow-hidden">
                  <img 
                    src={project.image} 
                    alt={project.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-dark)] via-transparent to-transparent"></div>
                  <div 
                    className="absolute top-4 left-4 px-4 py-2 rounded-full text-white text-sm font-semibold"
                    style={{ background: project.color }}
                  >
                    {project.category}
                  </div>
                </div>

                {/* Project Info */}
                <div className="p-6">
                  <h3 className="text-2xl font-bold text-[var(--text-primary)] mb-2 group-hover:text-[#3B82F6] transition-colors">
                    {project.title}
                  </h3>
                  <p className="text-[var(--text-muted)] mb-4 text-sm">{project.client}</p>
                  <p className="text-[var(--text-secondary)] mb-6 line-clamp-2">
                    {project.description}
                  </p>

                  {/* Key Results */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    {project.results.slice(0, 2).map((result, idx) => (
                      <div key={idx} className="text-center p-3 bg-[var(--bg-dark)] rounded-lg">
                        <div className="text-2xl font-bold" style={{ color: project.color }}>{result.metric}</div>
                        <div className="text-xs text-[var(--text-muted)]">{result.label}</div>
                      </div>
                    ))}
                  </div>

                  {/* Technologies */}
                  <div className="flex flex-wrap gap-2">
                    {project.technologies.slice(0, 4).map((tech, idx) => (
                      <span 
                        key={idx} 
                        className="px-3 py-1 bg-[var(--bg-dark)] text-[var(--text-muted)] text-xs rounded-full border border-[var(--border)]"
                      >
                        {tech}
                      </span>
                    ))}
                    {project.technologies.length > 4 && (
                      <span className="px-3 py-1 text-[var(--text-muted)] text-xs">
                        +{project.technologies.length - 4} more
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Project Detail Modal */}
      {selectedProject && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={() => setSelectedProject(null)}
        >
          <div 
            className="bg-[var(--bg-card)] border border-[var(--border)] rounded-2xl w-full max-w-4xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header Image */}
            <div className="relative h-64 md:h-80">
              <img 
                src={selectedProject.image} 
                alt={selectedProject.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-card)] via-transparent to-transparent"></div>
              <button
                onClick={() => setSelectedProject(null)}
                className="absolute top-4 right-4 w-10 h-10 bg-black/50 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-black/70 transition-colors"
              >
                ×
              </button>
            </div>

            <div className="p-8">
              {/* Project Header */}
              <div className="flex items-start justify-between mb-6">
                <div>
                  <span 
                    className="inline-block px-4 py-2 rounded-full text-white text-sm font-semibold mb-4"
                    style={{ background: selectedProject.color }}
                  >
                    {selectedProject.category}
                  </span>
                  <h2 className="text-3xl font-bold text-[var(--text-primary)] mb-2">{selectedProject.title}</h2>
                  <p className="text-[var(--text-muted)]">{selectedProject.client}</p>
                </div>
                <div className="flex items-center gap-2 text-[var(--text-muted)]">
                  <Clock size={18} />
                  <span>{selectedProject.duration}</span>
                </div>
              </div>

              {/* Challenge & Solution */}
              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="p-6 bg-[var(--bg-dark)] rounded-xl border border-[var(--border)]">
                  <h3 className="text-lg font-bold text-[#F97316] mb-3">The Challenge</h3>
                  <p className="text-[var(--text-secondary)]">{selectedProject.challenge}</p>
                </div>
                <div className="p-6 bg-[var(--bg-dark)] rounded-xl border border-[var(--border)]">
                  <h3 className="text-lg font-bold text-[#10B981] mb-3">Our Solution</h3>
                  <p className="text-[var(--text-secondary)]">{selectedProject.solution}</p>
                </div>
              </div>

              {/* Results */}
              <div className="mb-8">
                <h3 className="text-xl font-bold text-[var(--text-primary)] mb-4 flex items-center gap-2">
                  <TrendingUp size={24} className="text-[#3B82F6]" />
                  Results & Impact
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {selectedProject.results.map((result, idx) => (
                    <div key={idx} className="text-center p-4 bg-[var(--bg-dark)] rounded-xl border border-[var(--border)]">
                      <div className="text-3xl font-bold mb-1" style={{ color: selectedProject.color }}>{result.metric}</div>
                      <div className="text-sm text-[var(--text-muted)]">{result.label}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Testimonial */}
              <div className="p-6 bg-gradient-to-r from-[#3B82F6]/10 via-[#8B5CF6]/10 to-[#F97316]/10 rounded-xl border border-[var(--border)] mb-8">
                <div className="flex items-start gap-4">
                  <div className="text-4xl text-[#8B5CF6]">"</div>
                  <div>
                    <p className="text-[var(--text-primary)] text-lg italic mb-4">{selectedProject.testimonial.quote}</p>
                    <div>
                      <p className="text-[var(--text-primary)] font-semibold">{selectedProject.testimonial.author}</p>
                      <p className="text-[var(--text-muted)] text-sm">{selectedProject.testimonial.role}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Technologies */}
              <div className="mb-8">
                <h3 className="text-lg font-bold text-[var(--text-primary)] mb-4">Technologies Used</h3>
                <div className="flex flex-wrap gap-3">
                  {selectedProject.technologies.map((tech, idx) => (
                    <span 
                      key={idx}
                      className="px-4 py-2 bg-[var(--bg-dark)] text-[var(--text-secondary)] rounded-lg border border-[var(--border)]"
                    >
                      {tech}
                    </span>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={() => {
                    setSelectedProject(null);
                    navigate('/book-consultation');
                  }}
                  className="flex-1 py-4 text-lg font-semibold text-white rounded-xl transition-all hover:scale-105 hover:shadow-xl flex items-center justify-center gap-3"
                  style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}
                >
                  Start Your Project
                  <ArrowRight size={20} />
                </button>
                <button
                  onClick={() => setSelectedProject(null)}
                  className="px-8 py-4 text-lg font-semibold text-[var(--text-primary)] rounded-xl bg-[var(--bg-dark)] border border-[var(--border)] hover:border-[var(--border-hover)] transition-all"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CTA Section */}
      <section className="py-24 px-[7.6923%]" style={{ background: 'var(--bg-card)' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-[var(--text-primary)] mb-6">
            Ready to Build Your{' '}
            <span className="bg-gradient-to-r from-[#3B82F6] via-[#8B5CF6] to-[#F97316] bg-clip-text text-transparent">
              Success Story?
            </span>
          </h2>
          <p className="text-[var(--text-secondary)] text-lg mb-8 max-w-2xl mx-auto">
            Let's discuss how we can help transform your business with cutting-edge technology solutions.
          </p>
          <button
            onClick={() => navigate('/book-consultation')}
            className="px-8 py-4 text-lg font-semibold text-white rounded-xl transition-all duration-300 hover:scale-105 hover:shadow-xl hover:shadow-[#3B82F6]/25 inline-flex items-center gap-3"
            style={{ background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)' }}
          >
            Schedule Free Consultation
            <ArrowRight size={20} />
          </button>
        </div>
      </section>

      <Footer />
      <Chatbot proactiveDelay={30000} />
    </div>
  );
};

export default Portfolio;
